/**
 * In-browser API mock for the `fx-currency-expenses` prototype.
 *
 * Registers its routes with the shared dev mode (src/lib/dev-mode), so when the
 * auth bypass is active the Expenses screen runs with no Django backend.
 *
 * Scope is deliberately narrow: this prototype is about the currency of an
 * expense, so only Expenses and the catalogue the Add/Edit flow reaches for
 * (releases, tracks) are seeded. Every other screen keeps dev-mode's empty
 * default — the rest of the app is not part of this prototype.
 *
 * Exchange rates come from ./exchangeRates, the same module the Add / Edit
 * drawers preview with, so an expense is stored at exactly the rate the user
 * was shown. Rates are the server's job here: whatever a client sends for
 * exchange_rate / converted_amount is ignored and recomputed from the amount,
 * currency and date.
 */
import { API_BASE_URL } from "@/lib/api/core";
import { registerDevMockRoutes } from "@/lib/dev-mode";
import type { Expense } from "@/types/expenses";
import { LABEL_ID, RECORDINGS, RELEASES, recordingsForRelease, seedExpenses } from "./fixtures";
import {
  AMOUNT_DECIMALS,
  LABEL_CURRENCY,
  RATE_DECIMALS,
  convertToLabelCurrency,
} from "./exchangeRates";
import { INVOICE_PDF_URL } from "./invoicePdf";

// v2 carries the exchange rate and converted amount. Bumping the key drops a
// pre-FX list left in a reviewer's browser instead of showing it unconverted.
const STORE_KEY = "fxce-expenses-v2";
const STALE_STORE_KEYS = ["fxce-expenses-v1"];

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/** DRF-style page: the slice the app asked for, with the unsliced total. */
function paginated(results: unknown[], page = 1, pageSize = results.length): object {
  const start = (page - 1) * pageSize;
  const slice = results.slice(start, start + pageSize);
  return {
    results: slice,
    count: results.length,
    next: start + pageSize < results.length ? `?page=${page + 1}` : null,
    previous: page > 1 ? `?page=${page - 1}` : null,
  };
}

// --- Store -----------------------------------------------------------------
// Edits survive a reload (localStorage) so a reviewer can add an expense, look
// at it in the list, and still find it after refreshing. Reset clears the key.

function loadExpenses(): Expense[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw) as Expense[];
  } catch {
    /* corrupt or unavailable storage: fall back to a fresh seed */
  }
  const seeded = seedExpenses(INVOICE_PDF_URL);
  saveExpenses(seeded);
  return seeded;
}

function saveExpenses(expenses: Expense[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(expenses));
  } catch {
    /* private mode / quota: the prototype still works for this page view */
  }
}

/** Put the prototype back to its seeded state. Called by the dev bar. */
export function resetPrototype(): void {
  for (const key of [STORE_KEY, ...STALE_STORE_KEYS]) {
    localStorage.removeItem(key);
  }
}

// --- Applied-to ------------------------------------------------------------
// The API returns the nested release / track it was applied to; the form only
// sends an id, so the mock resolves it the same way the backend would.

function releaseDetails(id: string | null | undefined) {
  const release = RELEASES.find((r) => r.id === id);
  return release ? { id: release.id, title: release.title, artwork: null } : null;
}

function recordingDetails(id: string | null | undefined) {
  const recording = RECORDINGS.find((r) => r.id === id);
  if (!recording) return null;
  return {
    id: recording.id,
    title: recording.title,
    release_id: recording.releases[0]?.id ?? null,
  };
}

/**
 * Book the expense in the label currency at the rate for its own date. The
 * client's figures are never trusted: the rate is quoted here, so an expense
 * cannot end up stored at a rate nobody quoted.
 */
function withConversion(expense: Expense): Expense {
  const amount = expense.total_amount === null ? null : Number(expense.total_amount);
  const conversion = convertToLabelCurrency(amount, expense.currency, expense.date ?? null);
  return {
    ...expense,
    exchange_rate: conversion ? conversion.rate.toFixed(RATE_DECIMALS) : null,
    converted_amount: conversion ? conversion.converted.toFixed(AMOUNT_DECIMALS) : null,
  };
}

function withDetails(expense: Expense): Expense {
  return {
    ...expense,
    release_details: releaseDetails(expense.release),
    sound_recording_details: recordingDetails(expense.sound_recording),
  };
}

// --- Invoice extraction ----------------------------------------------------
// Uploading an invoice creates the expense, then the app polls it until
// extraction_status flips. Mocked as a short delay, then plausible values read
// "off the invoice" — including the currency, which today the Add flow throws
// away and replaces with the label currency.

const EXTRACTION_DELAY_MS = 2500;
const extractionDueAt = new Map<string, number>();

function extractedValues(name: string): Partial<Expense> {
  // Deterministic from the file name, so the same upload always extracts the same.
  const seed = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const amount = 250 + (seed % 3750);
  return {
    name: name.replace(/[-_]+/g, " ").trim(),
    total_amount: `${amount}.00`,
    currency: LABEL_CURRENCY,
    expense_type: "mastering",
  };
}

function applyExtractionIfDue(expense: Expense): Expense {
  const due = extractionDueAt.get(expense.id);
  if (!due || Date.now() < due) return expense;
  extractionDueAt.delete(expense.id);
  const extracted = {
    ...expense,
    ...extractedValues(expense.name),
    extraction_status: "extracted" as const,
  };
  const all = loadExpenses().map((e) => (e.id === extracted.id ? extracted : e));
  saveExpenses(all);
  return extracted;
}

// --- Routes ----------------------------------------------------------------

function nextId(): string {
  return `exp-${Math.random().toString(36).slice(2, 10)}`;
}

function toNumberString(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed.toFixed(2);
}

function listExpenses(query: URLSearchParams): Response {
  const search = (query.get("q") ?? "").trim().toLowerCase();
  const page = Number(query.get("page")) || 1;
  const pageSize = Number(query.get("page_size")) || 50;

  let expenses = loadExpenses().map(withDetails);
  if (search) {
    expenses = expenses.filter(
      (expense) =>
        expense.name.toLowerCase().includes(search) ||
        expense.release_details?.title.toLowerCase().includes(search) ||
        expense.sound_recording_details?.title.toLowerCase().includes(search),
    );
  }
  return jsonResponse(paginated(expenses, page, pageSize));
}

function createExpense(body: Record<string, unknown>): Response {
  const isUpload = Boolean(body.file);
  const expense: Expense = {
    id: nextId(),
    name: String(body.name ?? "Untitled expense"),
    date: (body.date as string) || undefined,
    expense_type: (body.expense_type as Expense["expense_type"]) ?? "other",
    total_amount: toNumberString(body.total_amount) ?? "0.00",
    currency: (body.currency as string) || LABEL_CURRENCY,
    release: (body.release as string) || undefined,
    sound_recording: (body.sound_recording as string) || undefined,
    release_details: null,
    sound_recording_details: null,
    exchange_rate: null,
    converted_amount: null,
    recoupable: body.recoupable === undefined ? true : String(body.recoupable) === "true",
    can_delete: true,
    file: isUpload ? INVOICE_PDF_URL : null,
    extraction_status: isUpload ? "processing" : null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const booked = withConversion(withDetails(expense));
  saveExpenses([booked, ...loadExpenses()]);
  if (isUpload) extractionDueAt.set(expense.id, Date.now() + EXTRACTION_DELAY_MS);
  return jsonResponse(booked, 201);
}

function updateExpense(id: string, body: Record<string, unknown>): Response {
  const expenses = loadExpenses();
  const existing = expenses.find((expense) => expense.id === id);
  if (!existing) return jsonResponse({ detail: "Not found." }, 404);

  const updated: Expense = withConversion(
    withDetails({
      ...existing,
      ...body,
      total_amount:
        body.total_amount === undefined ? existing.total_amount : toNumberString(body.total_amount),
      // The form clears "Applied to" by sending null; keep it untouched otherwise.
      release:
        body.release === undefined ? existing.release : ((body.release as string) ?? undefined),
      sound_recording:
        body.sound_recording === undefined
          ? existing.sound_recording
          : ((body.sound_recording as string) ?? undefined),
      extraction_status: null,
      updated_at: new Date().toISOString(),
    } as Expense),
  );

  saveExpenses(expenses.map((expense) => (expense.id === id ? updated : expense)));
  extractionDueAt.delete(id);
  return jsonResponse(updated);
}

function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // --- Expenses ------------------------------------------------------------
  if (trimmed === "/expenses") {
    if (method === "GET") return listExpenses(query);
    if (method === "POST") return createExpense(body ?? {});
  }
  if (trimmed.startsWith("/expenses/")) {
    const id = trimmed.slice("/expenses/".length);
    if (method === "GET") {
      const expense = loadExpenses().find((e) => e.id === id);
      if (!expense) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse(withDetails(applyExtractionIfDue(expense)));
    }
    if (method === "PATCH" || method === "PUT") return updateExpense(id, body ?? {});
    if (method === "DELETE") {
      saveExpenses(loadExpenses().filter((expense) => expense.id !== id));
      return jsonResponse(null, 204);
    }
  }

  // --- Catalogue (Add / Edit expense comboboxes) ----------------------------
  if (trimmed === "/catalogue/releases" && method === "GET") {
    const search = (query.get("q") ?? "").trim().toLowerCase();
    const releases = search
      ? RELEASES.filter(
          (release) =>
            release.title.toLowerCase().includes(search) ||
            release.catalog_number?.toLowerCase().includes(search),
        )
      : RELEASES;
    return jsonResponse(paginated(releases));
  }
  if (trimmed.endsWith("/recordings") && trimmed.startsWith("/catalogue/releases/")) {
    const releaseId = trimmed.slice("/catalogue/releases/".length, -"/recordings".length);
    return jsonResponse(paginated(recordingsForRelease(releaseId)));
  }
  if (trimmed === "/catalogue/recordings" && method === "GET") {
    const search = (query.get("q") ?? "").trim().toLowerCase();
    const recordings = search
      ? RECORDINGS.filter(
          (recording) =>
            recording.title.toLowerCase().includes(search) ||
            recording.isrc?.toLowerCase().includes(search),
        )
      : RECORDINGS;
    return jsonResponse(paginated(recordings));
  }

  // --- Label ---------------------------------------------------------------
  // Only so the label currency has one source; Settings itself is out of scope.
  if (trimmed === "/accounts/labels" && method === "GET") {
    return jsonResponse(paginated([{ id: LABEL_ID, name: "Dev Label", currency: LABEL_CURRENCY }]));
  }

  // Anything else belongs to a screen this prototype does not cover: fall
  // through to dev-mode's empty default rather than inventing data.
  return null;
}

/**
 * Hand the routes above to dev-mode's single fetch patch instead of installing
 * another one here. Returning null falls through to its empty default.
 */
registerDevMockRoutes((request) => {
  if (!request.url.startsWith(API_BASE_URL)) return null;

  const [path, queryString = ""] = request.url.slice(API_BASE_URL.length).split("?");
  const query = new URLSearchParams(queryString);

  let body: any;
  const rawBody = request.body;
  if (typeof rawBody === "string") {
    try {
      body = JSON.parse(rawBody);
    } catch {
      body = undefined;
    }
  } else if (rawBody instanceof FormData) {
    body = {};
    rawBody.forEach((value, key) => {
      body[key] = value instanceof File ? value.name : value;
    });
  }

  return route(request.method, path, body, query);
});
